import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { Checkbox } from '../../../components/ui/checkbox';

const graphOptions = [
  {
    title: 'Correct',
    color: 'var(--q3-green-normal)',
    checked: true,
  },
  {
    title: 'Incorrect',
    color: 'var(--q3-danger-normal)',
    checked: true,
  },
  {
    title: 'Unattempted',
    color: 'var(--q3-neutral-light)',
    checked: true,
  },
];

export default function DifficultyAnalysisChart({ data, selectedSubject }) {
  const [chartData, setChartData] = useState(null);
  const [selectedGraphOptions, setSelectedGraphOptions] = useState(graphOptions);
  const [yAxisDomain, setYAxisDomain] = useState([0, 100]);

  useEffect(() => {
    if (!data) return undefined;
    const subjectData = data[selectedSubject?.title?.toLowerCase()];
    if (!subjectData) return;

    const chartData = Object.entries(subjectData)
      .filter(([key]) => key !== 'subjectMeta' && key !== 'overall')
      .map(([difficulty, stats]) => ({
        difficulty: difficulty.charAt(0).toUpperCase() + difficulty.slice(1),
        Correct: stats?.correct || 0.15, // Use 0.15 to show a thin bar for 0 values
        Incorrect: stats?.incorrect || 0.15, // Use 0.15 to show a thin bar for 0 values
        Unattempted: stats?.unattempted || 0.15, // Use 0.15 to show a thin bar for 0 values
      }));

    setChartData(chartData);

    const maxCorrect = Math.max(...chartData.map((item) => item.Correct));
    const maxIncorrect = Math.max(...chartData.map((item) => item.Incorrect));
    const maxUnattempted = Math.max(...chartData.map((item) => item.Unattempted));
    const overallMax = Math.max(maxCorrect, maxIncorrect, maxUnattempted);
    setYAxisDomain([0, Math.ceil(overallMax * 1.1)]);
  }, [data, selectedSubject]);

  const handleOnChange = (value, clickIndex) => {
    const newSet = selectedGraphOptions.map((option, index) => ({
      ...option,
      checked: index === clickIndex ? !option.checked : option.checked,
    }));
    setSelectedGraphOptions(newSet);
  };

  const CustomBarLabel = ({ x, y, width, value }) => {
    const actual = Math.round(value);
    const displayValue = value === 0.15 ? 0 : actual;
    if (!displayValue && value !== 0.15) return null;

    return (
      <g transform={`translate(${x + width / 2}, ${y - 8})`} textAnchor="middle">
        <text
          style={{
            fontWeight: 'bold',
            fontSize: '14px',
            fill: 'var(--q3-neutral-default)',
          }}
        >
          {displayValue}
        </text>
      </g>
    );
  };

  if (!chartData) return null;

  const visibleGraphOptions = selectedGraphOptions.filter((option) => option.checked);

  return (
    <>
      <style jsx>{`
        .checkbox-custom[data-state='checked'] {
          background-color: var(--checkbox-bg) !important;
          border-color: var(--checkbox-border) !important;
        }
        .checkbox-custom:hover {
          border-color: var(--checkbox-border) !important;
        }
        .checkbox-custom:focus-visible {
          ring-color: var(--checkbox-border) !important;
        }
      `}</style>

      <div className="overflow-x-auto rounded-b-xl border-b border-[var(--q3-stroke-light)] pointer-events-none">
        <div className="rounded-b-xl bg-[var(--q3-surface-default)] shadow-none border border-y-0 border-[var(--q3-stroke-light)]">
          <div className="p-4">
            <div className="flex justify-start gap-4 flex-wrap mb-4">
              {selectedGraphOptions.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center cursor-pointer gap-2 border-b-2"
                  style={{ borderBottomColor: item.checked ? item.color : 'transparent' }}
                >
                  <Checkbox
                    id={index}
                    checked={item.checked}
                    onCheckedChange={(value) => handleOnChange(value, index)}
                    className="checkbox-custom cursor-pointer rounded-[4px] border-[var(--q3-stroke-normal)]"
                    style={{
                      '--checkbox-bg': item.color,
                      '--checkbox-border': item.color,
                    }}
                  />
                  <label htmlFor={index.toString()} className="text-[var(--q3-neutral-default)] cursor-pointer text-sm">
                    {item.title}
                  </label>
                </div>
              ))}
            </div>

            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} barSize={35} barGap={5} barCategoryGap="10%">
                <CartesianGrid strokeDasharray="3 3" stroke={'var(--q3-stroke-light)'} />
                <XAxis
                  dataKey="difficulty"
                  tick={{ fontSize: 14, fill: 'var(--q3-neutral-light)' }}
                  stroke={'var(--q3-stroke-light)'}
                  axisLine={{ stroke: 'var(--q3-stroke-light)' }}
                  interval={0}
                />
                <YAxis
                  domain={yAxisDomain}
                  tickFormatter={(value) => `${value} Qs`}
                  tick={{ fontSize: 12, fill: 'var(--q3-neutral-default)' }}
                  width={50}
                  stroke={'var(--q3-stroke-light)'}
                  axisLine={{ stroke: 'var(--q3-stroke-light)' }}
                />
                {visibleGraphOptions.map((item) => (
                  <Bar
                    key={item.title}
                    dataKey={item.title}
                    fill={item.color}
                    label={<CustomBarLabel />}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
}
